import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Workflow, Asset, Configuration } from '../../types';
import WorkflowOrganization from './WorkflowOrganization';
import CustomDropdown from '../gallery/CustomDropdown';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface WorkflowEditorProps {
  workflowId: string;
}

const WorkflowEditor: React.FC<WorkflowEditorProps> = ({ workflowId }) => {
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [currentConfig, setCurrentConfig] = useState<any>({});
  const [configName, setConfigName] = useState('');
  const [jobName, setJobName] = useState('');
  const [batchSize, setBatchSize] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkflow();
    fetchAssets();
    fetchConfigurations();
  }, [workflowId]);

  const fetchWorkflow = async () => {
    try {
      const response = await axios.get(`${API_BASE}/workflows/${workflowId}`);
      setWorkflow(response.data);

      const defaultConfig: any = {};
      Object.entries(response.data.configurable_params || {}).forEach(([key, config]: [string, any]) => {
        if (config.editable) {
          if (config.type === 'seed_control') {
            defaultConfig[`${key}_mode`] = config.default_mode || 'random';
            defaultConfig[`${key}_value`] = config.default_value || Math.floor(Math.random() * 0xFFFFFFFF);
          } else {
            defaultConfig[key] = config.default_value || '';
          }
        }
      });
      setCurrentConfig(defaultConfig);
    } catch (error) {
      console.error('Failed to fetch workflow:', error);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API_BASE}/assets`);
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    }
  };

  const fetchConfigurations = async () => {
    try {
      const response = await axios.get(`${API_BASE}/workflows/${workflowId}/configurations`);
      setConfigurations(response.data);
    } catch (error) {
      console.error('Failed to fetch configurations:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    if (!configName.trim()) {
      alert('Please enter a configuration name');
      return;
    }

    try {
      await axios.post(`${API_BASE}/configurations`, {
        workflow_id: workflowId,
        config_name: configName,
        parameter_values: currentConfig
      });

      setConfigName('');
      fetchConfigurations();
      alert('Configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save configuration:', error);
      alert('Failed to save configuration');
    }
  };

  const submitJob = async () => {
    if (!jobName.trim()) {
      alert('Please enter a job name');
      return;
    }

    if (batchSize < 1 || batchSize > 999999) {
      alert('Batch size must be between 1 and 999999');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/jobs`, {
        workflow_id: parseInt(workflowId),
        job_name: jobName,
        parameters: currentConfig,
        batch_size: batchSize
      });

      alert(`Job submitted successfully! ${batchSize > 1 ? `Generating ${batchSize} images` : 'Generating 1 image'}`);
      window.location.href = `/job/${response.data.id}`;
    } catch (error) {
      console.error('Failed to submit job:', error);
      alert('Failed to submit job');
    }
  };

  const toggleFavorite = async () => {
    if (!workflow) return;

    try {
      const response = await axios.post(`${API_BASE}/workflows/${workflow.id}/favorite`);
      setWorkflow({ ...workflow, is_favorite: response.data.is_favorite });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const loadConfiguration = (config: Configuration) => {
    setCurrentConfig(config.parameter_values);
  };

  const updateParameter = (key: string, value: any) => {
    setCurrentConfig({ ...currentConfig, [key]: value });
  };

  const updateWorkflow = (updatedWorkflow: Workflow) => {
    setWorkflow(updatedWorkflow);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Image Generation': return '🎨';
      case 'Text Processing': return '📝';
      case 'Video Generation': return '🎬';
      case 'Audio Processing': return '🎵';
      default: return '⚙️';
    }
  };

  const renderParameterInput = (key: string, config: any) => {
    if (!config.editable) return null;

    const value = currentConfig[key] || config.default_value || '';

    switch (config.type) {
      case 'seed_control':
        const seedMode = currentConfig[`${key}_mode`] || config.default_mode || 'random';
        const seedValue = currentConfig[`${key}_value`] || config.default_value || 0;

        const seedModeOptions = [
          { value: 'random', label: 'Random', icon: '🎲' },
          { value: 'fixed', label: 'Fixed', icon: '🔢' }
        ];

        return (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <div className="h-[40px] [&>button]:!h-[40px] [&>button]:!py-2">
                  <CustomDropdown
                    options={seedModeOptions}
                    value={seedMode}
                    onChange={(value) => {
                      if (value === 'random') {
                        // Update both mode and value in a single state update
                        setCurrentConfig({
                          ...currentConfig,
                          [`${key}_mode`]: value,
                          [`${key}_value`]: Math.floor(Math.random() * 0xFFFFFFFF)
                        });
                      } else {
                        updateParameter(`${key}_mode`, value);
                      }
                    }}
                  />
                </div>
              </div>

              {seedMode === 'fixed' && (
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    value={seedValue}
                    min="0"
                    max="4294967295"
                    onChange={(e) => updateParameter(`${key}_value`, parseInt(e.target.value) || 0)}
                    placeholder="Enter seed (0-4294967295)"
                  />
                </div>
              )}

              {seedMode === 'random' && (
                <div className="col-span-2 h-[40px] px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-sm flex items-center justify-between">
                  <span>Current: {seedValue}</span>
                  <button
                    type="button"
                    className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                    onClick={() => updateParameter(`${key}_value`, Math.floor(Math.random() * 0xFFFFFFFF))}
                  >
                    Regenerate
                  </button>
                </div>
              )}
            </div>
            {batchSize > 1 && (
              <div className="text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                <span className="font-medium">Batch Mode:</span> Each of the {batchSize} generations will use a unique random seed
              </div>
            )}
          </div>
        );

      case 'number':
        return (
          <input
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            min={config.min}
            max={config.max}
            step={config.step || 1}
            onChange={(e) => updateParameter(key, parseFloat(e.target.value))}
          />
        );
      case 'select':
        return (
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            onChange={(e) => updateParameter(key, e.target.value)}
          >
            {config.options?.map((option: string) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'asset':
        const filteredAssets = assets.filter(asset => asset.type === config.asset_type);
        return (
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            onChange={(e) => updateParameter(key, e.target.value)}
          >
            <option value="">Select {config.asset_type}...</option>
            {filteredAssets.map((asset) => (
              <option key={asset.id} value={asset.filename}>
                {asset.display_name}
              </option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            onChange={(e) => updateParameter(key, e.target.value)}
          />
        );
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-600">Loading workflow...</div>;
  }

  if (!workflow) {
    return <div className="text-center text-red-600 py-12">Workflow not found</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{getCategoryIcon(workflow.category)}</span>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">{workflow.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                  {workflow.category}
                </span>
                {workflow.tags && workflow.tags.length > 0 && (
                  <div className="flex gap-1">
                    {workflow.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={toggleFavorite}
            className={`p-2 rounded-full transition-colors ${
              workflow.is_favorite
                ? 'text-yellow-500 hover:text-yellow-600'
                : 'text-gray-400 hover:text-yellow-500'
            }`}
          >
            {workflow.is_favorite ? '⭐' : '☆'}
          </button>
        </div>
        <a href="/" className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors">
          ← Back to Workflows
        </a>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <h3 className="text-xl font-semibold text-gray-900">Parameters</h3>
          {Object.entries(workflow.configurable_params || {}).map(([key, config]: [string, any]) => (
            config.editable && (
              <div key={key} className="bg-gray-50 border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {config.display_name || key}
                </label>
                {config.description && (
                  <p className="text-xs text-gray-500 mb-3">{config.description}</p>
                )}
                {renderParameterInput(key, config)}
              </div>
            )
          ))}

          {/* Workflow Organization (Admin Only) */}
          <WorkflowOrganization
            workflow={workflow}
            onUpdate={updateWorkflow}
          />
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate</h3>

            <input
              type="text"
              placeholder="Job name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
              value={jobName}
              onChange={(e) => setJobName(e.target.value)}
            />

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Batch Size
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="1"
                  max="999999"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={batchSize}
                  onChange={(e) => setBatchSize(Math.max(1, Math.min(999999, parseInt(e.target.value) || 1)))}
                />
                <span className="text-sm text-gray-500">images</span>
              </div>
              {batchSize > 1 && (
                <p className="text-xs text-gray-600 mt-1">
                  Generate {batchSize} images with the same parameters but different random seeds
                </p>
              )}
            </div>

            <button
              onClick={submitJob}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-2 px-4 rounded-lg hover:from-green-600 hover:to-green-700 transition-all"
            >
              {batchSize > 1 ? `Generate ${batchSize} Images` : 'Submit Job'}
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Save Configuration</h3>
            <input
              type="text"
              placeholder="Configuration name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
            />
            <button
              onClick={saveConfiguration}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all"
            >
              Save Configuration
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Saved Configurations</h3>
            <div className="space-y-2">
              {configurations.map((config) => (
                <div
                  key={config.id}
                  className="bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg p-3 cursor-pointer transition-colors"
                  onClick={() => loadConfiguration(config)}
                >
                  <div className="font-medium text-sm text-gray-900">{config.config_name}</div>
                  <div className="text-xs text-gray-500">
                    {new Date(config.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowEditor;
